import { JSX } from 'react';

import { Button, ELanguage, InfoBlock, TTranslator } from '@smartshell/ui-kit';
import cn from 'classnames';

import { TLocalesKeys } from 'localization';
import { EAdvertisementStatus, IMyAdvertisement } from 'types';
import { formatExpirationDate } from 'utils';

import {
  IModalAdvertisementManagerState,
  IModalAdvertisementViewState,
} from '../../pages/Advertisement/interfaces';
import s from './AdvertisementViewModal.module.scss';

export const getStatusLabel = (
  t: TTranslator<TLocalesKeys>,
  lang: ELanguage,
  data?: IMyAdvertisement
): JSX.Element | undefined => {
  switch (data?.status) {
    case 'REJECTED':
      return (
        <InfoBlock
          className={cn(s.status_label)}
          color="red"
          title={t('canceled')}
          message={
            data?.moderation?.comment || t('advertisement.canceled.message')
          }
          iconName="CircleCloseIcon"
        />
      );
    case 'MODERATION':
      return (
        <InfoBlock
          className={cn(s.status_label)}
          color="orange"
          title={t('moderation')}
          message={t('advertisement.moderation.message')}
          iconName="ClockIcon"
        />
      );
    case 'ACTIVE':
      return (
        <InfoBlock
          className={cn(s.status_label, s.label_green)}
          title={`${t(
            'advertisement.status.label.active'
          )} ${formatExpirationDate(data?.expires_at, lang, t('until_2'))}`}
          iconName="OpenedEyeIcon"
        />
      );
    case 'COMPLETED':
      return (
        <InfoBlock
          className={cn(s.status_label, s.label_gray)}
          title={`${t('completed')} ${formatExpirationDate(
            data?.expires_at,
            lang
          )}`}
          iconName="CheckIcon"
        />
      );
    case 'EXPIRED':
      return (
        <InfoBlock
          className={cn(s.status_label, s.label_gray)}
          title={`${t(
            'advertisement.info.expired.title'
          )} ${formatExpirationDate(data?.expires_at, lang)}`}
          message={t('advertisement.info.expired.description')}
          iconName="ClockIcon"
        />
      );
  }
};

export const getActionButton = (
  t: TTranslator<TLocalesKeys>,
  setAdvertisementManagerState: React.Dispatch<
    React.SetStateAction<IModalAdvertisementManagerState | null>
  >,
  setAdvertisementViewState: React.Dispatch<
    React.SetStateAction<IModalAdvertisementViewState | null>
  >,
  setOpenUnpublishedModal: React.Dispatch<React.SetStateAction<boolean>>,
  handledRepublishAdvertisement: (id: string) => Promise<any>,
  data?: IMyAdvertisement,
  loading?: boolean,
  onClose?: () => void,
  disabled?: boolean
): JSX.Element => {
  switch (data?.status) {
    case EAdvertisementStatus.ACTIVE:
      return (
        <Button
          className={cn(s.button, s.unpublished)}
          iconName="EyeNoIcon"
          onClick={(e) => {
            e.stopPropagation();

            setOpenUnpublishedModal(true);
          }}
          isLoading={loading}
          disabled={disabled}
        >
          {t('advertisement.info.button.unpublished')}
        </Button>
      );
    case EAdvertisementStatus.MODERATION:
      return (
        <Button
          className={s.button}
          iconName="EditIcon"
          onClick={(e) => {
            e.stopPropagation();
            setAdvertisementViewState(null);
            setAdvertisementManagerState({
              mode: 'edit',
              advertisement: data,
            });
          }}
          disabled={disabled}
        >
          {t('edit')}
        </Button>
      );
    case EAdvertisementStatus.REJECTED:
      return (
        <Button
          className={s.button}
          iconName="EditIcon"
          onClick={(e) => {
            e.stopPropagation();
            setAdvertisementViewState(null);
            setAdvertisementManagerState({
              mode: 'edit',
              advertisement: data,
            });
          }}
          disabled={disabled}
        >
          {t('edit')}
        </Button>
      );
    case EAdvertisementStatus.COMPLETED:
    case EAdvertisementStatus.EXPIRED:
      return (
        <Button
          className={cn(s.button, s.republished)}
          iconName="OpenedEyeIcon"
          onClick={(e) => {
            e.stopPropagation();
            handledRepublishAdvertisement(data?.id).then(() => onClose?.());
          }}
          isLoading={loading}
          disabled={disabled}
        >
          {t('advertisement.info.button.published')}
        </Button>
      );
    default:
      return (
        <Button className={s.button} disabled={disabled} iconName="EditIcon">
          {t('edit')}
        </Button>
      );
  }
};

export const getDropdownTextAction = (
  t: TTranslator<TLocalesKeys>,
  status?: EAdvertisementStatus
): string => {
  switch (status) {
    case EAdvertisementStatus.ACTIVE:
      return t('advertisement.info.button.unpublished');

    case EAdvertisementStatus.COMPLETED:
    case EAdvertisementStatus.REJECTED:
    case EAdvertisementStatus.EXPIRED:
      return t('advertisement.info.button.published');

    default:
      return t('edit');
  }
};
